import { trackHomeEvent } from './variants';
import { heroWallSrcsetFromJpg, pickBgWebpUrl } from './image-srcset';
import { getWallsForVariant, resolveVariantKey } from './variants';

const SECTION_TARGETS = [
    { section: 'how', selector: '#how, #howHtj' },
    { section: 'compare', selector: '.m-cmp-sec' },
    { section: 'ai', selector: '.m-ai' },
    { section: 'use_case', selector: '#hUse' },
    { section: 'account', selector: '#hAcct' },
    { section: 'reviews', selector: '.m-quote' },
    { section: 'pricing', selector: '#pricing' },
    { section: 'faq', selector: '#value .m-faq, #faqHtj' },
    { section: 'end', selector: '.m-end' },
];

function initHomeDiscoveryTracking(vKey) {
    if ('IntersectionObserver' in window) {
        const seen = new Set();
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                const section = e.target.dataset.homeSection;
                if (!section || seen.has(section) || e.intersectionRatio < 0.5) return;
                seen.add(section);
                trackHomeEvent('home_section_viewed', { section });
            });
        }, { threshold: 0.5 });
        SECTION_TARGETS.forEach(({ section, selector }) => {
            document.querySelectorAll(selector).forEach((el) => {
                if (el.hidden || el.offsetParent === null) return;
                el.dataset.homeSection = section;
                io.observe(el);
            });
        });
    }

    document.querySelectorAll('#value .m-faq details summary').forEach((summary) => {
        summary.addEventListener('click', () => {
            const question = summary.textContent?.trim();
            if (!question) return;
            trackHomeEvent('home_faq_opened', { question });
        });
    });

    if (vKey !== 'htj') {
        const billing = () => (document.querySelector('.bill [data-bill][aria-checked="true"]')?.dataset.bill === 'month' ? 'monthly' : 'yearly');
        document.querySelectorAll('#pricing .pbtn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const href = btn.getAttribute('href') || '';
                let plan = 'free';
                if (/Get Pro/i.test(btn.textContent || '')) plan = 'pro';
                else if (/Get Studio/i.test(btn.textContent || '')) plan = 'studio';
                else if (btn.hasAttribute('data-start') || href === '#send') plan = 'free';
                trackHomeEvent('home_plan_clicked', { plan, billing: billing() });
            });
        });
    }
}

const ASSET = '/static/homepage';

function $(id) { return document.getElementById(id); }

function assetUrl(name) {
    if (!name) return '';
    if (name.startsWith('/')) return name;
    if (name.endsWith('.png')) return `${ASSET}/${name}`;
    const bare = name.replace(/\.jpe?g$/i, '');
    if (/^[hpgre]\d+$/i.test(bare)) return `${ASSET}/${bare}.jpg`;
    if (bare.startsWith('g/')) return `${ASSET}/${bare.replace(/\.jpe?g$/i, '.webp')}`;
    return `${ASSET}/${name.replace(/\.jpe?g$/i, '.webp')}`;
}

export function initPublicHomeMarketing() {
    document.querySelectorAll('[data-bg]').forEach((el) => {
        const raw = el.getAttribute('data-bg');
        if (raw && !raw.startsWith('/')) el.setAttribute('data-bg', assetUrl(raw));
    });

    const lazy = Array.from(document.querySelectorAll('[data-bg]'));
    const load = (el) => {
        const url = el.getAttribute('data-bg');
        const w = el.clientWidth || el.offsetWidth;
        const picked = pickBgWebpUrl(url, w);
        el.style.backgroundImage = `url('${picked}')`;
        el.removeAttribute('data-bg');
    };
    if (!('IntersectionObserver' in window)) {
        lazy.forEach(load);
    } else {
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    load(e.target);
                    io.unobserve(e.target);
                }
            });
        }, { rootMargin: '600px 0px' });
        lazy.forEach((el) => io.observe(el));
    }

    const vKey = resolveVariantKey();
    initHomeDiscoveryTracking(vKey);
    const WALLS = getWallsForVariant(vKey);
    if (!WALLS.length && vKey === 'htj') document.body.classList.add('no-walls');

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let wi = Math.floor(Math.random() * WALLS.length);
    let front = $('wallA');
    let back = $('wallB');
    if (!front || !back) return;

    function credit(w) {
        const label = (w.video && !w.placeholder) ? 'Film by ' : 'Photo by ';
        if ($('creditBy')) $('creditBy').textContent = `${label}${w.by}`;
        if ($('credit')) {
            $('credit').href = w.url;
            const em = $('credit').querySelector('em');
            if (em) em.textContent = w.note || 'A Friend of JPEGmini';
        }
    }

    function fillImage(slot, w, pos, go) {
        const im = document.createElement('img');
        im.alt = '';
        im.style.width = '100%';
        im.style.height = '100%';
        im.style.objectFit = 'cover';
        im.style.objectPosition = pos;
        im.onload = go;
        im.onerror = go;
        const srcKey = w.poster || w.src;
        im.src = assetUrl(srcKey);
        const srcset = w.src ? heroWallSrcsetFromJpg(w.src) : heroWallSrcsetFromJpg(srcKey);
        if (srcset) {
            im.srcset = srcset;
            im.sizes = '100vw';
        }
        slot.appendChild(im);
        if (im.complete) go();
    }

    function fill(slot, w, ready) {
        slot.innerHTML = '';
        let once = false;
        const go = () => { if (!once) { once = true; ready?.(); } };
        const phone = window.matchMedia('(max-width: 640px)').matches;
        const pos = (phone && w.posM) ? w.posM : w.pos;

        if (w.video && !still) {
            const v = document.createElement('video');
            v.muted = true;
            v.loop = true;
            v.playsInline = true;
            v.autoplay = true;
            v.setAttribute('playsinline', '');
            v.style.width = '100%';
            v.style.height = '100%';
            v.style.objectFit = 'cover';
            v.style.objectPosition = pos;
            if (w.poster) v.poster = assetUrl(w.poster);
            v.addEventListener('loadeddata', go);
            v.addEventListener('error', () => {
                slot.innerHTML = '';
                fillImage(slot, w, pos, go);
            });
            v.src = `${ASSET}/${w.video}`;
            slot.appendChild(v);
            const pr = v.play();
            if (pr?.catch) pr.catch(() => {
                slot.innerHTML = '';
                fillImage(slot, w, pos, go);
            });
            setTimeout(go, 2500);
            return;
        }

        fillImage(slot, w, pos, go);
        setTimeout(go, 2500);
    }

    if (WALLS.length) {
        fill(front, WALLS[wi]);
        credit(WALLS[wi]);
    }

    if (WALLS.length > 1 && !still) {
        const next = () => {
            setTimeout(() => {
                if (document.hidden || !$('pAdd') || $('pAdd').hidden) return next();
                wi = (wi + 1) % WALLS.length;
                const w = WALLS[wi];
                fill(back, w, () => {
                    back.classList.add('on');
                    front.classList.remove('on');
                    const t = front;
                    front = back;
                    back = t;
                    credit(w);
                    setTimeout(() => { back.innerHTML = ''; }, 1600);
                    next();
                });
            }, 9000);
        };
        next();
    }

    window.addEventListener('scroll', () => {
        const top = document.querySelector('.top');
        if (!top) return;
        top.classList.toggle('solid', window.scrollY > 40);
    }, { passive: true });

    document.querySelectorAll('.bill [data-bill]').forEach((b) => {
        b.addEventListener('click', () => {
            const m = b.dataset.bill === 'month' ? 'm' : 'y';
            document.querySelectorAll('.bill [data-bill]').forEach((x) => {
                x.setAttribute('aria-checked', x === b ? 'true' : 'false');
            });
            document.querySelectorAll('#pricing [data-y]').forEach((n) => {
                n.textContent = n.getAttribute(`data-${m}`);
            });
            const note = $('billNote');
            if (note) {
                note.textContent = m === 'm' ? 'Monthly plans, cancel anytime.' : '14-day refund on annual plans. Cancel anytime.';
            }
        });
    });

    const fold = $('fold');
    if (fold && 'IntersectionObserver' in window && !still) {
        const cells = Array.from(fold.querySelectorAll('.fs-after b[data-from]'));
        const total = fold.querySelector('.fs-after .fs-tag strong');
        const fmt = (v) => `${(v >= 20 ? Math.round(v) : v.toFixed(1))} MB`;
        cells.forEach((c) => { c.textContent = fmt(+c.dataset.from); });
        if (total) total.textContent = '4.5 GB';
        new IntersectionObserver((entries, o) => {
            entries.forEach((e) => {
                if (!e.isIntersecting) return;
                o.disconnect();
                const t0 = Date.now();
                const D = 1400;
                const step = () => {
                    const k = Math.min(1, (Date.now() - t0) / D);
                    const ease = 1 - Math.pow(1 - k, 3);
                    cells.forEach((c) => {
                        const a = +c.dataset.from;
                        const b = +c.dataset.to;
                        c.textContent = fmt(a + (b - a) * ease);
                    });
                    if (total) total.textContent = `${(4.5 + (1.8 - 4.5) * ease).toFixed(1)} GB`;
                    if (k < 1) requestAnimationFrame(step);
                };
                step();
            });
        }, { threshold: 0.5 }).observe(fold);
    }

    const sc = $('phScroll2');
    const sec = document.querySelector('.m-watch');
    if (sc && sec && !still) {
        let tick = false;
        const move = () => {
            tick = false;
            const r = sec.getBoundingClientRect();
            const vh = window.innerHeight;
            let span = Math.max(1, r.height - vh);
            let p = Math.max(0, Math.min(1, -r.top / span));
            if (window.matchMedia('(max-width: 1080px)').matches) {
                p = Math.max(0, Math.min(1, (vh * 0.6 - r.top) / r.height));
            }
            const max = Math.max(0, sc.scrollHeight - sc.parentNode.clientHeight + 40);
            sc.style.transform = `translateY(${-p * max}px)`;
        };
        window.addEventListener('scroll', () => {
            if (!tick) { tick = true; requestAnimationFrame(move); }
        }, { passive: true });
        window.addEventListener('resize', move);
        move();
    }

    const chat = $('chat');
    if (chat && !still && 'IntersectionObserver' in window) {
        chat.classList.add('wait');
        new IntersectionObserver((entries, o) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    chat.classList.remove('wait');
                    chat.classList.add('play');
                    setTimeout(() => chat.classList.add('done'), 2000);
                    o.disconnect();
                }
            });
        }, { threshold: 0.6 }).observe(chat);
    }
}
